import axios from "axios";
import type { AvailableNumberSearchResult } from "../signup-prototype/availableNumbers";
import { validateNumberSearchResult } from "../signup-prototype/availableNumbers.ts";

export interface VerificationScope {
  businessId: string;
  userId: string;
}
export type VerificationDraft = Record<string, string>;
export interface VerificationOffer {
  version: string | number;
  priceId: string;
  name: string;
  amountMinor: number;
  currency: string;
  interval: "month";
  features: string[];
  taxMode: "automatic";
  monthlyMessageLimit: number;
  overageUnitAmount: number;
}
export interface VerificationSnapshot {
  enabled?: boolean;
  environment?: "production" | "staging";
  simulated?: boolean;
  canResume?: boolean;
  canCancel?: boolean;
  version: 1;
  revision: number;
  state:
    | "draft"
    | "submitted"
    | "working"
    | "pending_approval"
    | "correction_required"
    | "payment_required"
    | "active"
    | "cancelled"
    | "support_required";
  business: { id: string; name: string; country: "US" | "CA"; phone?: string; areaCode?: string };
  draft: VerificationDraft;
  offer: VerificationOffer | null;
  card: { status: "empty" | "saved"; brand?: string; last4?: string };
  number: { selected?: string; assigned?: string; locked?: boolean };
  registration: {
    status: string;
    message?: string;
    rejection?: { id: string; stage: string; code: string; message: string; fields: string[]; mode: "self_serve" | "assisted"; canCorrect: boolean };
    correction?: { status: "editing" | "submitted" | "assistance_requested" | "completed"; generation: number; mode: "self_serve" | "assisted"; fields: string[] };
  };
  readiness: { approved: boolean; ready: boolean };
  billing: {
    status:
      | "not_started"
      | "processing"
      | "requires_action"
      | "payment_failed"
      | "paid"
      | "cancelled";
  };
  error?: {
    code?: string;
    message?: string;
    fieldErrors?: Record<string, string>;
  };
}
export type VerificationResponse = VerificationSnapshot | { enabled: false };
export interface PaymentSetup {
  snapshot: VerificationSnapshot;
  setupIntentId: string;
  clientSecret: string;
}
export interface PaymentRecovery {
  snapshot: VerificationSnapshot;
  clientSecret?: string;
}
export class VerificationError extends Error {
  code: string;
  status?: number;
  fieldErrors?: Record<string, string>;
  constructor(
    message: string,
    code = "UNAVAILABLE",
    status?: number,
    fieldErrors?: Record<string, string>,
  ) {
    super(message);
    this.code = code;
    this.status = status;
    this.fieldErrors = fieldErrors;
  }
}
type Transport = (
  method: "GET" | "POST",
  url: string,
  body: unknown,
  config: {
    signal?: AbortSignal;
    timeout: number;
    headers: Record<string, string>;
    _suppressErrorToast: true;
  },
) => Promise<unknown>;
const object = (value: unknown): value is Record<string, any> =>
  !!value && typeof value === "object" && !Array.isArray(value);
const states = [
  "draft",
  "submitted",
  "working",
  "pending_approval",
  "correction_required",
  "payment_required",
  "active",
  "cancelled",
  "support_required",
];
export function validateVerification(
  value: unknown,
  scope: VerificationScope,
): VerificationResponse {
  if (object(value) && value.enabled === false) return { enabled: false };
  if (
    !object(value) ||
    value.version !== 1 ||
    !Number.isSafeInteger(value.revision) ||
    value.revision < 0 ||
    !states.includes(value.state) ||
    (value.canResume !== undefined && typeof value.canResume !== "boolean") ||
    (value.canCancel !== undefined && typeof value.canCancel !== "boolean") ||
    !object(value.business) ||
    value.business.id !== scope.businessId ||
    !["US", "CA"].includes(value.business.country) ||
    typeof value.business.name !== "string" ||
    (value.business.phone !== undefined && typeof value.business.phone !== "string") ||
    (value.business.areaCode !== undefined && (typeof value.business.areaCode !== "string" || !/^[2-9][0-9]{2}$/.test(value.business.areaCode))) ||
    !object(value.draft) ||
    Object.values(value.draft).some((item) => typeof item !== "string") ||
    !object(value.card) ||
    !["empty", "saved"].includes(value.card.status) ||
    !object(value.number) ||
    (value.number.locked !== undefined && typeof value.number.locked !== "boolean") ||
    !object(value.registration) ||
    typeof value.registration.status !== "string" ||
    !object(value.readiness) ||
    typeof value.readiness.approved !== "boolean" ||
    typeof value.readiness.ready !== "boolean" ||
    !object(value.billing) ||
    ![
      "not_started",
      "processing",
      "requires_action",
      "payment_failed",
      "paid",
      "cancelled",
    ].includes(value.billing.status)
  ) {
    throw new VerificationError(
      "We couldn’t confirm your setup details. Please reload and try again.",
      "INVALID_RESPONSE",
    );
  }
  const rejection = value.registration.rejection;
  const correction = value.registration.correction;
  if ((rejection !== undefined && (!object(rejection) || !["id", "stage", "code", "message"].every(key => typeof rejection[key] === "string") || !Array.isArray(rejection.fields) || rejection.fields.some((field: unknown) => typeof field !== "string") || !["self_serve", "assisted"].includes(rejection.mode) || typeof rejection.canCorrect !== "boolean")) ||
    (correction !== undefined && (!object(correction) || !["editing", "submitted", "assistance_requested", "completed"].includes(correction.status) || !Number.isSafeInteger(correction.generation) || correction.generation < 1 || !["self_serve", "assisted"].includes(correction.mode) || !Array.isArray(correction.fields) || correction.fields.some((field: unknown) => typeof field !== "string"))))
    throw new VerificationError("We couldn’t confirm the requested corrections. Please reload your setup.", "INVALID_RESPONSE");
  if (
    value.offer !== null &&
    (!object(value.offer) ||
      !["string", "number"].includes(typeof value.offer.version) ||
      typeof value.offer.priceId !== "string" ||
      typeof value.offer.name !== "string" ||
      !Number.isSafeInteger(value.offer.amountMinor) ||
      value.offer.amountMinor < 0 ||
      !/^[A-Za-z]{3}$/.test(value.offer.currency) ||
      value.offer.interval !== "month" ||
      value.offer.taxMode !== "automatic" ||
      !Number.isSafeInteger(value.offer.monthlyMessageLimit) ||
      value.offer.monthlyMessageLimit < 0 ||
      !Number.isSafeInteger(value.offer.overageUnitAmount) ||
      value.offer.overageUnitAmount < 0 ||
      !Array.isArray(value.offer.features) ||
      value.offer.features.some(
        (feature: unknown) => typeof feature !== "string",
      ))
  ) {
    throw new VerificationError(
      "We couldn’t confirm your plan. Please reload and try again.",
      "INVALID_OFFER",
    );
  }
  for (const phone of [value.number.selected, value.number.assigned])
    if (
      phone !== undefined &&
      (typeof phone !== "string" || !/^\+1\d{10}$/.test(phone))
    )
      throw new VerificationError(
        "We couldn’t confirm your number.",
        "INVALID_RESPONSE",
      );
  if (
    value.card.status === "saved" &&
    (typeof value.card.brand !== "string" ||
      !/^\d{4}$/.test(value.card.last4 || ""))
  )
    throw new VerificationError(
      "We couldn’t confirm your saved card.",
      "INVALID_RESPONSE",
    );
  return value as VerificationSnapshot;
}
const axiosTransport: Transport = async (method, url, body, config) =>
  (method === "GET"
    ? await axios.get(url, config)
    : await axios.post(url, body, config)
  ).data;
export function createVerificationApi(
  scope: VerificationScope,
  transport: Transport = axiosTransport,
) {
  const request = async (
    method: "GET" | "POST",
    path = "",
    body?: unknown,
    signal?: AbortSignal,
  ) => {
    try {
      return await transport(
        method,
        `/api/onboarding/verification${path}`,
        body,
        {
          signal,
          timeout: 25000,
          _suppressErrorToast: true,
          headers: {
            "X-Onboarding-Business": scope.businessId,
            "X-Onboarding-User": scope.userId,
          },
        },
      );
    } catch (reason) {
      if (signal?.aborted) throw reason;
      const error = reason as {
        response?: {
          status?: number;
          data?: {
            error?: {
              code?: string;
              message?: string;
              fieldErrors?: Record<string, string>;
            };
          };
        };
      };
      throw new VerificationError(
        error.response?.data?.error?.message ||
          "We couldn’t save that step. Please check your connection and try again.",
        error.response?.data?.error?.code,
        error.response?.status,
        error.response?.data?.error?.fieldErrors,
      );
    }
  };
  const snapshot = async (
    path = "",
    body?: unknown,
    signal?: AbortSignal,
  ): Promise<VerificationResponse> =>
    validateVerification(
      await request(body === undefined ? "GET" : "POST", path, body, signal),
      scope,
    );
  const paymentResponse = async <T extends PaymentSetup | PaymentRecovery>(
    path: string,
    body: unknown,
    signal?: AbortSignal,
  ): Promise<T> => {
    const data = await request("POST", path, body, signal);
    if (
      !object(data) ||
      !("version" in validateVerification(data.snapshot, scope)) ||
      (data.clientSecret !== undefined &&
        typeof data.clientSecret !== "string") ||
      (path.endsWith("/setup") &&
        (!/^seti_[A-Za-z0-9]+$/.test(data.setupIntentId || "") ||
          !data.clientSecret))
    )
      throw new VerificationError(
        "We couldn’t prepare your payment method. Please try again.",
        "INVALID_RESPONSE",
      );
    return data as T;
  };
  return {
    load: (signal?: AbortSignal) => snapshot("", undefined, signal),
    setup: (revision: number, signal?: AbortSignal) =>
      paymentResponse<PaymentSetup>("/payment/setup", { revision }, signal),
    confirm: (setupIntentId: string, signal?: AbortSignal) =>
      snapshot("/payment/confirm", { setupIntentId }, signal),
    save: (revision: number, draft: VerificationDraft, signal?: AbortSignal) =>
      snapshot("/draft", { revision, draft }, signal),
    selectNumber: (
      revision: number,
      phoneNumber: string,
      signal?: AbortSignal,
    ) => snapshot("/number", { revision, phoneNumber }, signal),
    submit: (
      revision: number,
      offerVersion: string | number,
      signal?: AbortSignal,
      correctionGeneration?: number,
    ) =>
      snapshot(
        "/submit",
        {
          revision,
          offerVersion,
          accepted: true,
          authorizedRepresentative: true,
          customerConsent: true,
          ...(correctionGeneration !== undefined ? { correctionGeneration } : {}),
        },
        signal,
      ),
    refresh: (signal?: AbortSignal) => snapshot("/refresh", {}, signal),
    recover: (signal?: AbortSignal) =>
      paymentResponse<PaymentRecovery>("/payment/recover", {}, signal),
    resume: (revision: number, signal?: AbortSignal) =>
      snapshot("/resume", { revision }, signal),
    startCorrection: (revision: number, signal?: AbortSignal) =>
      snapshot("/correction/start", { revision }, signal),
    cancel: (revision: number, signal?: AbortSignal) =>
      snapshot("/cancel", { revision }, signal),
    numbers: async (
      country: "US" | "CA",
      areaCode = "",
      signal?: AbortSignal,
    ): Promise<AvailableNumberSearchResult> =>
      validateNumberSearchResult(
        await request(
          "GET",
          `/numbers${areaCode ? `?areaCode=${encodeURIComponent(areaCode)}` : ""}`,
          undefined,
          signal,
        ),
        { country, ...(areaCode ? { areaCode } : {}) },
      ),
  };
}
export type VerificationApi = ReturnType<typeof createVerificationApi>;
export function formatOffer(
  offer: VerificationOffer,
  amountMinor = offer.amountMinor,
) {
  return `${new Intl.NumberFormat(undefined, { style: "currency", currency: offer.currency.toUpperCase() }).format(amountMinor / 100)} ${offer.currency.toUpperCase()}`;
}
export function messageAllowance(offer: VerificationOffer) {
  return `${new Intl.NumberFormat().format(offer.monthlyMessageLimit)} messages per month included. Additional messages are ${formatOffer(offer, offer.overageUnitAmount)} each.`;
}
